import { useEffect, useState } from 'react';
import { SubmissionMode } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { Template } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { AppointmentsApiQueries, AppointmentsApiTypes } from '@frontend/api-appointments';
import { DigitalFormsMutations } from '@frontend/api-digital-forms';
import { PersonQueries, PersonsV3 } from '@frontend/api-person';
import { AppointmentSelectorComponents } from '@frontend/appointment-selector';
import { withFormsFreemium } from '@frontend/digital-forms-scope';
import {
  FormPacketSelectorComponents,
  FormPacketSelectorHooks,
  FormPacketSelectorTypes,
} from '@frontend/form-packet-selector';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { ModalControlWithKeepState } from '@frontend/integrated-messaging';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Modal, useModalControl } from '@frontend/design-system';
import { useTemplateMessagingStore } from '../stores/template-selector-store';
import { TemplateSelectorModal } from './template-selector-modal';

type AppointmentSelection = AppointmentsApiTypes.ListAppointmentsIO['output']['appointments'][number];
type InsertArgs = {
  appointmentId?: string;
  appointmentAt?: string;
  submissionId: string;
  formOrPacketLink: string;
  template?: Template;
};
type FormsTemplateModalProps = ModalControlWithKeepState & {
  groupId: string;
  personId?: string;
  personPhone: string;
  onInsert: (args: InsertArgs) => void;
};

const FormsTemplateModalComponent = ({
  groupId,
  personId,
  personPhone,
  onInsert,
  show,
  ...rest
}: FormsTemplateModalProps) => {
  const { t } = useTranslation('inbox-templates');
  const modalControl = useModalControl();
  const isLargeMode = useMatchMedia({ minWidth: breakpoints.xsmall.max });
  const { setSelectedMessageTemplateType } = useTemplateMessagingStore('setSelectedMessageTemplateType');
  const personQuery = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId: personId ?? '',
      locationIds: [groupId],
    },
    {
      enabled: !!personId,
    }
  );
  const appointmentsQuery = AppointmentsApiQueries.useListPersonAppointments(
    {
      locationId: groupId,
      personId: personId ?? '',
    },
    {
      enabled: !!personId,
      placeholderData: {
        appointments: [],
      },
      retry: 1,
    }
  );
  const householdQuery = PersonQueries.useGetPersonHouseholds(personQuery.data?.householdId ?? '', {
    disabled: !personId || !personQuery.data?.householdId,
    locationId: groupId,
  });
  const allowAppointmentSelection =
    !!personId && (appointmentsQuery.isFetched ? !!appointmentsQuery.data?.appointments.length : true);
  const [currentStep, setCurrentStep] = useState<'appointment' | 'forms' | 'template'>('forms');
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentSelection>();
  const [selectedFormOrPacket, setSelectedFormOrPacket] = useState<FormPacketSelectorTypes.FormOrPacketV2>();
  const {
    selectorProps,
    selectedItem,
    selectedCategory,
    reset: resetFormPacketList,
  } = FormPacketSelectorHooks.useFormPacketSelector({
    groupId,
  });

  const handleAppointmentSelection = (appt: AppointmentSelection) => {
    setSelectedAppointment(appt);
    handleInsert(selectedItem, undefined, appt);
  };

  const handleFormOrPacketSelection = (
    formOrPacket: FormPacketSelectorTypes.FormOrPacketV2,
    selectTemplate?: boolean
  ) => {
    setSelectedFormOrPacket(formOrPacket);
    if (selectTemplate) {
      setCurrentStep('template');
      return;
    }

    handleInsert(formOrPacket);
  };

  const { isLoading, mutateAsync } = DigitalFormsMutations.useCreateFormLinks();

  const handleInsert = async (
    formOrPacket: FormPacketSelectorTypes.FormOrPacketV2 | undefined = selectedFormOrPacket,
    template?: Template,
    customAppointment?: AppointmentSelection
  ) => {
    if (!formOrPacket) return;
    const appointment = selectedAppointment || customAppointment;
    const linkRes = await mutateAsync({
      companyId: formOrPacket.companyId || groupId,
      patients: personId
        ? [
            {
              id: personId,
              firstName: personQuery.data?.preferredName || personQuery.data?.firstName,
              lastName: personQuery.data?.lastName,
              birthdate: personQuery.data?.birthdate,
              email: personQuery.data?.contactInfo?.find((c) => c.type === ContactType_Enum.EMAIL)?.destination,
              appointmentId: appointment?.appointmentId,
              phoneNumber: personPhone,
            },
          ]
        : [],
      sendId: formOrPacket.id,
      type: selectedCategory === FormPacketSelectorTypes.Category.FORMS ? 'form' : 'packet',
      familyHead: householdQuery.data?.find((h) => h.is_guardian)?.id,
      mode: SubmissionMode.SOLICITED_DIRECT,
    });
    const linkToSend = linkRes.data?.find((link) => !!link.submitUrl);
    if (formOrPacket.id && linkToSend?.submitUrl)
      onInsert({
        appointmentId: appointment?.appointmentId,
        appointmentAt: appointment?.appointmentAt,
        submissionId: linkToSend.token ?? '',
        formOrPacketLink: linkToSend.submitUrl,
        template,
      });

    rest.onClose();
  };

  useEffect(() => {
    if (currentStep === 'appointment' && !allowAppointmentSelection) {
      setCurrentStep('forms');
    }
  }, [allowAppointmentSelection]);

  useEffect(() => {
    if (show === false) {
      setSelectedAppointment(undefined);
      setSelectedFormOrPacket(undefined);
      setCurrentStep(allowAppointmentSelection ? 'appointment' : 'forms');
      resetFormPacketList();
    }
  }, [show]);
  const booleanShow = show === 'hidden' ? false : show;

  return (
    <TemplateSelectorModal {...modalControl.modalProps} show={booleanShow}>
      <ContentLoader show={isLoading} />
      {currentStep === 'appointment' && (
        <AppointmentSelectorComponents.AppointmentSelectorModalContentsV2
          groupId={groupId}
          personId={personId!}
          onSelect={handleAppointmentSelection}
          onSkip={() => setCurrentStep('forms')}
          onClose={rest.onClose}
          onBack={() => setCurrentStep('forms')}
        />
      )}
      {currentStep === 'forms' && (
        <>
          <Modal.Header onClose={rest.onClose}>
            <Button
              aria-label='close'
              onClick={() => setSelectedMessageTemplateType()}
              iconName='back-small'
              variant='secondary'
            />
            {t('Select Packet or Form')}
          </Modal.Header>
          <FormPacketSelectorComponents.FormPacketSelectorModalContents {...selectorProps} />
          <Modal.StickyFooter
            css={isLargeMode && { padding: theme.spacing(4), boxShadow: 'none', justifyContent: 'flex-end' }}
          >
            <div
              css={[
                {
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr',
                  gap: theme.spacing(1.25),
                },
                isLargeMode && {
                  gridTemplateColumns: 'auto auto',
                },
              ]}
            >
              <Button
                variant='secondary'
                disabled={!selectedItem || !allowAppointmentSelection}
                onClick={() => setCurrentStep('appointment')}
              >
                <Icon name='calendar-add-small' />
                {t('Link To Appointment')}
              </Button>

              <Button disabled={!selectedItem} size='small' onClick={() => handleFormOrPacketSelection(selectedItem!)}>
                {t('Insert')}
              </Button>
            </div>
          </Modal.StickyFooter>
        </>
      )}
    </TemplateSelectorModal>
  );
};

export const FormsTemplateModal = withFormsFreemium(FormsTemplateModalComponent);
